import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import {
  Shield, Users, Coins, RefreshCw, Search, Loader2, ArrowDownToLine,
  Wallet, Settings as SettingsIcon, CheckCircle2, X, Copy, Save,
  Zap, TrendingUp, Plus, Minus, Power, Menu, LayoutDashboard, Layers, Ban, ShieldCheck,
} from "lucide-react";
import {
  adminOverview, adminUsers, recalcRewards,
  adminWithdrawals, adminMarkWithdraw, adminDeposits, adminGetSettings, adminSetSettings,
  adminVerifyAuto, adminAdjust, adminBlock, getTopDepositors,
  getAdminKey, setAdminKey, clearAdminKey, adminAuthNonce, adminAuthVerify,
} from "@/lib/api";
import { getActiveSigner } from "@/lib/wallet";

const short = (a) => (a ? `${a.slice(0, 6)}…${a.slice(-4)}` : "-");
const usd = (n) => `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
const ADJ_CATS = [
  { k: "direct", label: "Direct Reward" }, { k: "roi", label: "ROI Income" },
  { k: "level", label: "Level Income" }, { k: "daily", label: "Daily Pool" },
  { k: "weekly", label: "Weekly Pool" }, { k: "monthly", label: "Monthly Pool" },
  { k: "top", label: "Top Depositor" },
  { k: "stake", label: "Stake (grows 2x cap)" },
];
const NAV = [
  { id: "dashboard", label: "Dashboard", icon: LayoutDashboard },
  { id: "users", label: "User Management", icon: Users },
  { id: "adjustment", label: "Adjustment", icon: Plus },
  { id: "deposits", label: "Deposits", icon: Wallet },
  { id: "withdrawals", label: "Withdrawals", icon: ArrowDownToLine },
  { id: "pools", label: "Pools", icon: Layers },
  { id: "settings", label: "Settings", icon: SettingsIcon },
];

function Card({ children, className = "" }) {
  return <div className={`rounded-2xl border border-white/10 bg-[#0A1F14]/80 p-5 ${className}`}>{children}</div>;
}
function Stat({ icon: Icon, label, value, accent }) {
  return (
    <Card>
      <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-white/50" data-testid={`admin-stat-${label}`}>
        <Icon size={14} className={accent} /> {label}
      </div>
      <div className="mt-2 font-mono text-2xl font-bold text-white">{value}</div>
    </Card>
  );
}
function PoolCard({ label, value }) {
  return (
    <Card>
      <div className="text-xs uppercase tracking-wider text-white/50">{label}</div>
      <div className="mt-2 font-mono text-2xl font-bold text-[#7CF3A0]">{value}</div>
    </Card>
  );
}

export default function AdminPanel() {
  const [unlocked, setUnlocked] = useState(!!getAdminKey());
  const [unlocking, setUnlocking] = useState(false);
  const [view, setView] = useState("dashboard");
  const [menuOpen, setMenuOpen] = useState(false);
  const [ov, setOv] = useState(null);
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState("");
  const [wds, setWds] = useState([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [deposits, setDeposits] = useState(null);
  const [settings, setSettings] = useState(null);
  const [topPool, setTopPool] = useState(null);
  const [adjustFor, setAdjustFor] = useState(null);
  const [adjustCat, setAdjustCat] = useState("direct");
  const [adjustAmt, setAdjustAmt] = useState("");
  const [adjAddr, setAdjAddr] = useState("");

  const loadOverview = useCallback(async () => { try { setOv(await adminOverview()); } catch (e) {} }, []);
  const loadUsers = useCallback(async (query = "") => {
    try { const d = await adminUsers({ q: query, limit: 50 }); setRows(d.rows); } catch (e) { toast.error("Users load failed"); }
  }, []);
  const loadWithdrawals = useCallback(async () => {
    try { const d = await adminWithdrawals(); setWds(d.rows || []); setPendingCount(d.pending_count || 0); } catch (e) {}
  }, []);
  const loadDeposits = useCallback(async () => { try { setDeposits(await adminDeposits()); } catch (e) {} }, []);
  const loadSettings = useCallback(async () => { try { setSettings(await adminGetSettings()); } catch (e) {} }, []);
  const loadPools = useCallback(async () => { try { setTopPool(await getTopDepositors()); } catch (e) {} }, []);

  useEffect(() => { if (unlocked) { loadOverview(); loadWithdrawals(); } }, [unlocked, loadOverview, loadWithdrawals]);
  useEffect(() => {
    if (!unlocked) return;
    if (view === "users") loadUsers(q);
    if (view === "deposits") loadDeposits();
    if (view === "settings") loadSettings();
    if (view === "adjustment") loadUsers("");
    if (view === "pools") { loadPools(); loadOverview(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, unlocked]);

  const doUnlock = async () => {
    setUnlocking(true);
    try {
      const signer = await getActiveSigner();
      const address = await signer.getAddress();
      const { message } = await adminAuthNonce();
      const signature = await signer.signMessage(message);
      const { token } = await adminAuthVerify({ address, message, signature });
      setAdminKey(token);
      await adminOverview();
      setUnlocked(true);
      toast.success("Admin unlocked");
    } catch (e) {
      clearAdminKey();
      toast.error(e?.response?.data?.detail || e?.message || "Access denied");
    } finally { setUnlocking(false); }
  };
  const doLock = () => { clearAdminKey(); setUnlocked(false); };

  if (!unlocked) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0a0e14] px-4" data-testid="admin-lock-screen">
        <div className="w-full max-w-sm rounded-2xl border border-white/10 bg-[#111827] p-6 text-center shadow-xl">
          <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-[#0AA84F] to-[#D6C51E]">
            <ShieldCheck size={26} className="text-black" />
          </div>
          <h1 className="text-xl font-semibold text-white">TITAN Admin</h1>
          <p className="mt-1 text-sm text-white/50">Connect the admin wallet and sign to enter. No other wallet can access this panel.</p>
          <button
            onClick={doUnlock}
            disabled={unlocking}
            data-testid="admin-unlock-btn"
            className="mt-5 w-full rounded-xl bg-gradient-to-r from-[#0AA84F] to-[#D6C51E] py-2.5 text-sm font-bold text-black active:scale-[0.98] disabled:opacity-50"
          >
            {unlocking ? "Waiting for signature…" : "Connect wallet & sign"}
          </button>
        </div>
      </div>
    );
  }

  const go = (id) => { setView(id); setMenuOpen(false); };

  const doRecalc = async () => {
    setBusy("recalc");
    try { await recalcRewards(); toast.success("Rewards recalculated"); await loadOverview(); if (view === "users") await loadUsers(q); }
    catch (e) { toast.error("Recalc failed"); } finally { setBusy(""); }
  };
  const markPaid = async (w) => {
    const hash = window.prompt(`Approve payout of $${w.net_usd} USDT to ${w.to_wallet}.\n\n• Leave EMPTY = send automatically on-chain now.\n• Or paste a tx hash if you already paid manually.`, "");
    if (hash === null) return;
    setBusy(`paid-${w.id}`);
    try {
      const res = await adminMarkWithdraw(w.id, { status: "paid", tx_hash: hash || null });
      const tx = res?.withdrawal?.tx_hash;
      toast.success(tx ? `Paid · tx ${tx.slice(0, 10)}…` : "Marked as paid");
      await loadWithdrawals();
    }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setBusy(""); }
  };
  const reject = async (w) => {
    if (!window.confirm(`Reject this $${w.amount_usd} withdrawal? The amount is refunded to the user's balance.`)) return;
    setBusy(`rej-${w.id}`);
    try { await adminMarkWithdraw(w.id, { status: "rejected" }); toast.success("Rejected & refunded"); await loadWithdrawals(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setBusy(""); }
  };
  const toggleAuto = async (r) => {
    setBusy(`auto-${r.address}`);
    try { await adminVerifyAuto({ address: r.address, enabled: !r.auto_withdraw }); toast.success(!r.auto_withdraw ? `Auto ON · ${r.uid}` : `Auto OFF · ${r.uid}`); await loadUsers(q); await loadOverview(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setBusy(""); }
  };
  const toggleBlock = async (r) => {
    if (!window.confirm(`${r.is_blocked ? "Unblock" : "Block"} ${r.uid}? ${r.is_blocked ? "" : "They won't be able to deposit or withdraw."}`)) return;
    setBusy(`block-${r.address}`);
    try { await adminBlock({ address: r.address, blocked: !r.is_blocked }); toast.success(!r.is_blocked ? `Blocked · ${r.uid}` : `Unblocked · ${r.uid}`); await loadUsers(q); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setBusy(""); }
  };
  const doAdjust = async (address, cat, amt, op = "add") => {
    const val = Number(amt);
    if (!address || !val || val <= 0) { toast.error("Enter a valid address and amount"); return; }
    setBusy(op === "deduct" ? "deduct" : "adjust");
    try {
      const r = await adminAdjust({ address: address.trim().toLowerCase(), amount: val, category: cat, op });
      toast.success(`${op === "deduct" ? "Deducted" : "Funded"} ${usd(val)} ${op === "deduct" ? "from" : "to"} ${r.uid} · auto-withdrawal OFF`);
      setAdjustFor(null); setAdjustAmt(""); setAdjustCat("direct"); setAdjAddr("");
      if (view === "users" || view === "adjustment") await loadUsers(q);
      await loadOverview();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setBusy(""); }
  };
  const saveSettings = async () => {
    setBusy("settings");
    try {
      setSettings(await adminSetSettings({
        min_deposit_usd: Number(settings.min_deposit_usd), min_withdraw_usd: Number(settings.min_withdraw_usd),
        withdraw_fee_pct: Number(settings.withdraw_fee_pct),
      }));
      toast.success("Settings saved");
    } catch (e) { toast.error("Save failed"); } finally { setBusy(""); }
  };

  const pools = ov?.pools || {};

  return (
    <div className="min-h-screen w-full bg-[#04110A] text-white">
      {/* Top bar */}
      <div className="sticky top-0 z-40 flex items-center justify-between border-b border-white/10 bg-[#04110A]/95 px-4 py-3 backdrop-blur">
        <div className="flex items-center gap-3">
          <button data-testid="admin-menu-btn" onClick={() => setMenuOpen(true)} className="flex h-10 w-10 items-center justify-center rounded-xl border border-white/10 bg-[#0A1F14] text-[#7CF3A0] active:scale-95">
            <Menu size={20} />
          </button>
          <div className="flex items-center gap-2">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-gradient-to-br from-[#0AA84F] to-[#FFA000]"><Shield size={18} className="text-black" /></div>
            <div>
              <div className="text-base font-extrabold leading-tight">TITAN Admin</div>
              <div className="text-[10px] text-white/40">{NAV.find((n) => n.id === view)?.label}</div>
            </div>
          </div>
        </div>
        <button data-testid="admin-recalc-btn" onClick={doRecalc} disabled={busy === "recalc"} className="inline-flex items-center gap-2 rounded-full border border-[#0AA84F]/40 bg-[#0AA84F]/15 px-4 py-2 text-xs font-semibold text-[#7CF3A0] disabled:opacity-60">
          {busy === "recalc" ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />} Recalc
        </button>
      </div>

      {/* Side drawer */}
      {menuOpen && (
        <div className="fixed inset-0 z-50 flex" onClick={() => setMenuOpen(false)}>
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
          <div data-testid="admin-drawer" className="relative h-full w-72 max-w-[80%] border-r border-white/10 bg-[#07160E] p-4" onClick={(e) => e.stopPropagation()}>
            <div className="mb-5 flex items-center justify-between">
              <div className="flex items-center gap-2 font-extrabold"><Shield size={18} className="text-[#7CF3A0]" /> TITAN Admin</div>
              <button onClick={() => setMenuOpen(false)} className="flex h-8 w-8 items-center justify-center rounded-full bg-white/5 text-white/60"><X size={16} /></button>
            </div>
            <nav className="flex flex-col gap-1.5">
              {NAV.map((n) => (
                <button key={n.id} data-testid={`admin-nav-${n.id}`} onClick={() => go(n.id)}
                  className={`flex items-center gap-3 rounded-xl px-4 py-3 text-sm font-semibold transition ${view === n.id ? "bg-[#0AA84F]/20 text-[#7CF3A0]" : "text-white/60 hover:bg-white/5"}`}>
                  <n.icon size={17} /> {n.label}
                  {n.id === "withdrawals" && pendingCount > 0 && <span className="ml-auto rounded-full bg-red-500 px-1.5 text-[10px] font-bold text-white">{pendingCount}</span>}
                </button>
              ))}
            </nav>
            <button data-testid="admin-lock-btn" onClick={doLock}
              className="mt-4 flex w-full items-center gap-3 rounded-xl px-4 py-3 text-sm font-semibold text-white/60 transition hover:bg-white/5">
              <Power size={17} /> Lock
            </button>
          </div>
        </div>
      )}

      <div className="mx-auto max-w-6xl px-4 py-6">
        {/* Dashboard: only summary cards */}
        {view === "dashboard" && (
          <div data-testid="admin-view-dashboard" className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            <Stat icon={Users} label="Users" value={ov?.user_count ?? "—"} accent="text-[#7CF3A0]" />
            <Stat icon={CheckCircle2} label="Active" value={ov?.active_count ?? "—"} accent="text-[#7CF3A0]" />
            <Stat icon={Coins} label="Total Deposited" value={ov ? usd(ov.total_staked_usd) : "—"} accent="text-[#FFC24B]" />
            <Stat icon={ArrowDownToLine} label="Pending Payouts" value={pendingCount} accent="text-[#FFC24B]" />
            <Stat icon={Zap} label="Auto WD ON" value={ov?.auto_withdraw_on_count ?? "—"} accent="text-[#7CF3A0]" />
            <Stat icon={TrendingUp} label="Daily Earning" value={ov ? usd(ov.daily_earning_total_usd) : "—"} accent="text-[#FFC24B]" />
          </div>
        )}

        {/* User Management */}
        {view === "users" && (
          <Card data-testid="admin-view-users">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-2 text-sm font-bold text-white"><Users size={16} className="text-[#7CF3A0]" /> User Management</div>
              <div className="flex items-center gap-2 rounded-full border border-white/10 bg-black/30 px-3 py-1.5">
                <Search size={14} className="text-white/40" />
                <input data-testid="admin-user-search" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && loadUsers(q)} placeholder="Search address or UID…" className="w-48 bg-transparent text-sm text-white outline-none placeholder:text-white/30" />
                <button data-testid="admin-search-btn" onClick={() => loadUsers(q)} className="text-xs font-semibold text-[#7CF3A0]">Go</button>
              </div>
            </div>
            <div className="mt-4 overflow-x-auto">
              <table className="w-full min-w-[820px] text-left text-sm">
                <thead className="text-[11px] uppercase tracking-wider text-white/40">
                  <tr className="border-b border-white/10"><th className="py-2 pr-3">UID / Address</th><th className="py-2 pr-3">Deposited</th><th className="py-2 pr-3">Cap</th><th className="py-2 pr-3">Auto WD</th><th className="py-2 pr-3">Status</th><th className="py-2 pr-0 text-right">Actions</th></tr>
                </thead>
                <tbody data-testid="admin-users-table">
                  {rows.map((r) => (
                    <tr key={r.address} className="border-b border-white/5" data-testid={`admin-row-${r.uid}`}>
                      <td className="py-2.5 pr-3">
                        <div className="font-semibold text-white">{r.uid || "—"}</div>
                        <div className="flex items-center gap-1 font-mono text-[11px] text-white/40">{short(r.address)}
                          <button data-testid={`admin-copy-${r.uid}`} onClick={() => { navigator.clipboard.writeText(r.address); toast.success("Address copied"); }} className="text-[#7CF3A0]"><Copy size={11} /></button>
                        </div>
                      </td>
                      <td className="py-2.5 pr-3 font-mono text-white/80">{usd(r.stake_usd)}</td>
                      <td className="py-2.5 pr-3 font-mono text-white/80">{usd(r.mining_cap_usd)}</td>
                      <td className="py-2.5 pr-3">
                        <button data-testid={`admin-auto-toggle-${r.uid}`} onClick={() => toggleAuto(r)} disabled={busy === `auto-${r.address}`}
                          className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-bold ${r.auto_withdraw ? "bg-[#0AA84F]/20 text-[#7CF3A0]" : "bg-white/5 text-white/40"}`}><Power size={11} /> {r.auto_withdraw ? "ON" : "OFF"}</button>
                      </td>
                      <td className="py-2.5 pr-3">
                        {r.is_blocked
                          ? <span className="rounded-full bg-red-500/20 px-2 py-0.5 text-[11px] font-semibold text-red-300">Blocked</span>
                          : <span className="rounded-full bg-[#0AA84F]/15 px-2 py-0.5 text-[11px] font-semibold text-[#7CF3A0]">Active</span>}
                      </td>
                      <td className="py-2.5 pr-0">
                        <div className="flex items-center justify-end gap-1.5">
                          <button data-testid={`admin-fund-${r.uid}`} onClick={() => { setAdjustFor(r); setAdjustCat("direct"); setAdjustAmt(""); }} className="inline-flex items-center gap-1 rounded-lg border border-[#0AA84F]/30 bg-[#0AA84F]/10 px-2 py-1.5 text-[11px] font-bold text-[#7CF3A0]"><Plus size={12} /> Fund</button>
                          <button data-testid={`admin-block-${r.uid}`} onClick={() => toggleBlock(r)} disabled={busy === `block-${r.address}`}
                            className={`inline-flex items-center gap-1 rounded-lg border px-2 py-1.5 text-[11px] font-bold ${r.is_blocked ? "border-[#0AA84F]/30 bg-[#0AA84F]/10 text-[#7CF3A0]" : "border-red-500/30 bg-red-500/10 text-red-300"}`}>
                            {r.is_blocked ? <><ShieldCheck size={12} /> Unblock</> : <><Ban size={12} /> Block</>}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {rows.length === 0 && <tr><td colSpan={6} className="py-8 text-center text-sm text-white/40">No users found.</td></tr>}
                </tbody>
              </table>
            </div>
          </Card>
        )}

        {/* Adjustment */}
        {view === "adjustment" && (
          <Card data-testid="admin-view-adjustment" className="max-w-lg">
            <div className="mb-1 flex items-center gap-2 text-sm font-bold text-white"><Plus size={16} className="text-[#7CF3A0]" /> Wallet Adjustment Fund</div>
            <p className="mb-4 text-[11px] text-white/45">Credit any fund directly to a user. It's fully withdrawable, sits outside the 2x cap, and pays no upline. The user's auto-withdrawal turns OFF (paid only after your approval).</p>
            <label className="mb-1 block text-xs text-white/50">User wallet address</label>
            <input data-testid="adjust-address" value={adjAddr} onChange={(e) => setAdjAddr(e.target.value)} placeholder="0x…" className="mb-3 w-full rounded-xl border border-white/10 bg-black/40 px-3 py-2.5 font-mono text-xs text-white outline-none" />
            <label className="mb-1 block text-xs text-white/50">Fund type</label>
            <select data-testid="adjust-category" value={adjustCat} onChange={(e) => setAdjustCat(e.target.value)} className="mb-3 w-full rounded-xl border border-white/10 bg-black/40 px-3 py-2.5 text-sm text-white outline-none">
              {ADJ_CATS.map((c) => <option key={c.k} value={c.k}>{c.label}</option>)}
            </select>
            <label className="mb-1 block text-xs text-white/50">Amount (USDT)</label>
            <input data-testid="adjust-amount" type="number" min="0" step="0.01" value={adjustAmt} onChange={(e) => setAdjustAmt(e.target.value)} placeholder="0.00" className="mb-4 w-full rounded-xl border border-white/10 bg-black/40 px-3 py-2.5 text-sm text-white outline-none" />
            <button data-testid="adjust-submit" onClick={() => doAdjust(adjAddr, adjustCat, adjustAmt, "add")} disabled={busy === "adjust" || busy === "deduct"} className="flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-[#0AA84F] to-[#FFA000] py-2.5 text-sm font-bold text-black disabled:opacity-60">
              {busy === "adjust" ? <Loader2 size={15} className="animate-spin" /> : <Plus size={15} />} Add Fund
            </button>
            <button data-testid="adjust-deduct" onClick={() => doAdjust(adjAddr, adjustCat, adjustAmt, "deduct")} disabled={busy === "adjust" || busy === "deduct"} className="mt-2 flex w-full items-center justify-center gap-2 rounded-xl border border-[#E5484D]/60 bg-[#E5484D]/12 py-2.5 text-sm font-bold text-[#FF8A8D] disabled:opacity-60">
              {busy === "deduct" ? <Loader2 size={15} className="animate-spin" /> : <Minus size={15} />} Deduct Fund
            </button>
          </Card>
        )}

        {/* Deposits */}
        {view === "deposits" && (
          <Card data-testid="admin-view-deposits">
            <div className="mb-4 flex items-center justify-between">
              <div className="flex items-center gap-2 text-sm font-bold text-white"><Wallet size={16} className="text-[#7CF3A0]" /> Confirmed Deposits</div>
              <div className="text-sm font-bold text-[#FFC24B]">Total: {usd(deposits?.total_deposited_usd)}</div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[640px] text-left text-sm">
                <thead className="text-[11px] uppercase tracking-wider text-white/40"><tr className="border-b border-white/10"><th className="py-2 pr-3">Date · UID</th><th className="py-2 pr-3">From</th><th className="py-2 pr-3">Amount</th><th className="py-2 pr-0 text-right">Tx</th></tr></thead>
                <tbody data-testid="admin-deposits-table">
                  {(deposits?.rows || []).map((d) => (
                    <tr key={d.tx_hash} className="border-b border-white/5">
                      <td className="py-2.5 pr-3"><div className="text-white/80">{(d.created_at || "").slice(0, 10)}</div><div className="text-[11px] text-white/40">{d.uid}</div></td>
                      <td className="py-2.5 pr-3 font-mono text-[11px] text-white/70">{short(d.from_addr)}</td>
                      <td className="py-2.5 pr-3 font-mono font-bold text-[#7CF3A0]">{usd(d.amount_usd)}</td>
                      <td className="py-2.5 pr-0 text-right font-mono text-[11px] text-white/50">{short(d.tx_hash)}</td>
                    </tr>
                  ))}
                  {(!deposits?.rows || deposits.rows.length === 0) && <tr><td colSpan={4} className="py-8 text-center text-sm text-white/40">No deposits yet.</td></tr>}
                </tbody>
              </table>
            </div>
          </Card>
        )}

        {/* Withdrawals */}
        {view === "withdrawals" && (
          <Card data-testid="admin-view-withdrawals">
            <div className="mb-4 flex items-center justify-between">
              <div className="flex items-center gap-2 text-sm font-bold text-white"><ArrowDownToLine size={16} className="text-[#7CF3A0]" /> Withdrawal Requests</div>
              <button data-testid="admin-refresh-withdrawals" onClick={loadWithdrawals} className="text-xs font-semibold text-[#7CF3A0]">Refresh</button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[820px] text-left text-sm">
                <thead className="text-[11px] uppercase tracking-wider text-white/40"><tr className="border-b border-white/10"><th className="py-2 pr-3">Date · UID</th><th className="py-2 pr-3">Wallet</th><th className="py-2 pr-3">Amount</th><th className="py-2 pr-3">Fee</th><th className="py-2 pr-3">Net (send)</th><th className="py-2 pr-3">Status</th><th className="py-2 pr-0 text-right">Actions</th></tr></thead>
                <tbody data-testid="admin-withdrawals-table">
                  {wds.map((w) => (
                    <tr key={w.id} className="border-b border-white/5" data-testid={`admin-wd-${w.id}`}>
                      <td className="py-2.5 pr-3"><div className="text-white/80">{(w.created_at || "").slice(0, 10)}</div><div className="text-[11px] text-white/40">{w.uid}</div></td>
                      <td className="py-2.5 pr-3"><div className="flex items-center gap-1 font-mono text-[11px] text-white/70">{short(w.to_wallet)}<button onClick={() => { navigator.clipboard.writeText(w.to_wallet); toast.success("Copied"); }} className="text-[#7CF3A0]"><Copy size={11} /></button></div></td>
                      <td className="py-2.5 pr-3 font-mono text-white/80">{usd(w.amount_usd)}</td>
                      <td className="py-2.5 pr-3 font-mono text-red-300">{usd(w.fee_usd)}</td>
                      <td className="py-2.5 pr-3 font-mono font-bold text-[#7CF3A0]">{usd(w.net_usd)}</td>
                      <td className="py-2.5 pr-3"><span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${w.status === "paid" ? "bg-[#0AA84F]/20 text-[#7CF3A0]" : w.status === "rejected" ? "bg-red-500/20 text-red-300" : "bg-[#FFA000]/20 text-[#FFC24B]"}`}>{w.status}</span></td>
                      <td className="py-2.5 pr-0">
                        {w.status === "pending" ? (
                          <div className="flex items-center justify-end gap-1.5">
                            <button data-testid={`admin-pay-${w.id}`} onClick={() => markPaid(w)} disabled={busy === `paid-${w.id}`} className="rounded-lg border border-[#0AA84F]/30 bg-[#0AA84F]/10 px-2.5 py-1.5 text-[11px] font-bold text-[#7CF3A0] disabled:opacity-50">Mark Paid</button>
                            <button data-testid={`admin-reject-${w.id}`} onClick={() => reject(w)} disabled={busy === `rej-${w.id}`} className="rounded-lg border border-red-500/30 bg-red-500/10 p-1.5 text-red-300 disabled:opacity-50"><X size={14} /></button>
                          </div>
                        ) : <span className="text-[11px] text-white/30">{w.tx_hash ? short(w.tx_hash) : "—"}</span>}
                      </td>
                    </tr>
                  ))}
                  {wds.length === 0 && <tr><td colSpan={7} className="py-8 text-center text-sm text-white/40">No withdrawal requests.</td></tr>}
                </tbody>
              </table>
            </div>
          </Card>
        )}

        {/* Pools */}
        {view === "pools" && (
          <div data-testid="admin-view-pools" className="grid grid-cols-2 gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <PoolCard label="Daily Pool" value={usd(pools.daily)} />
            <PoolCard label="Weekly Pool" value={usd(pools.weekly)} />
            <PoolCard label="Monthly Pool" value={usd(pools.monthly)} />
            <PoolCard label="Top Depositor Pool" value={usd(topPool?.pool_balance)} />
          </div>
        )}

        {/* Settings */}
        {view === "settings" && settings && (
          <Card data-testid="admin-view-settings" className="max-w-lg">
            <div className="mb-4 flex items-center gap-2 text-sm font-bold text-white"><SettingsIcon size={16} className="text-[#7CF3A0]" /> Platform Settings</div>
            {[
              { k: "admin_wallet", label: "Admin USDT deposit wallet (BEP-20)", mono: true, locked: true },
              { k: "usdt_contract", label: "USDT contract address", mono: true, locked: true },
              { k: "min_deposit_usd", label: "Minimum deposit (USD)", num: true },
              { k: "min_withdraw_usd", label: "Minimum withdrawal (USD)", num: true },
              { k: "withdraw_fee_pct", label: "Withdrawal fee (%)", num: true },
            ].map((f) => (
              <div key={f.k} className="mb-3">
                <label className="mb-1 flex items-center gap-1.5 text-xs text-white/50">
                  {f.label}
                  {f.locked && <span className="rounded bg-[#D6C51E]/15 px-1.5 py-0.5 text-[10px] font-bold text-[#D6C51E]">🔒 LOCKED</span>}
                </label>
                <input
                  data-testid={`admin-setting-${f.k}`}
                  type={f.num ? "number" : "text"}
                  value={settings[f.k] ?? ""}
                  readOnly={f.locked}
                  onChange={(e) => !f.locked && setSettings({ ...settings, [f.k]: e.target.value })}
                  className={`w-full rounded-xl border border-white/10 px-3 py-2.5 text-sm outline-none ${f.mono ? "font-mono text-xs" : ""} ${f.locked ? "cursor-not-allowed bg-black/50 text-white/45" : "bg-black/30 text-white"}`}
                />
                {f.locked && <p className="mt-1 text-[10px] text-white/35">Hard-locked in code — cannot be changed by anyone.</p>}
              </div>
            ))}
            <button data-testid="admin-save-settings-btn" onClick={saveSettings} disabled={busy === "settings"} className="mt-2 inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-[#0AA84F] to-[#FFA000] px-5 py-2.5 text-sm font-bold text-black disabled:opacity-60">
              {busy === "settings" ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Save Settings
            </button>
          </Card>
        )}
      </div>

      {/* Fund modal (from User Management) */}
      {adjustFor && (
        <div data-testid="admin-fund-modal" className="fixed inset-0 z-[200] flex items-center justify-center bg-black/80 p-4" onClick={() => setAdjustFor(null)}>
          <div className="w-full max-w-sm rounded-2xl border border-[#0AA84F]/40 bg-[#0A1F14] p-5" onClick={(e) => e.stopPropagation()}>
            <div className="mb-1 flex items-center justify-between">
              <div className="text-base font-bold text-white">Wallet Adjustment Fund</div>
              <button onClick={() => setAdjustFor(null)} className="flex h-8 w-8 items-center justify-center rounded-full bg-white/5 text-white/60"><X size={16} /></button>
            </div>
            <div className="mb-4 font-mono text-[11px] text-white/50">{adjustFor.uid} · {short(adjustFor.address)}</div>
            <label className="mb-1 block text-xs text-white/50">Fund type</label>
            <select data-testid="admin-fund-category" value={adjustCat} onChange={(e) => setAdjustCat(e.target.value)} className="mb-3 w-full rounded-xl border border-white/10 bg-black/40 px-3 py-2.5 text-sm text-white outline-none">
              {ADJ_CATS.map((c) => <option key={c.k} value={c.k}>{c.label}</option>)}
            </select>
            <label className="mb-1 block text-xs text-white/50">Amount (USDT)</label>
            <input data-testid="admin-fund-amount" type="number" min="0" step="0.01" value={adjustAmt} onChange={(e) => setAdjustAmt(e.target.value)} placeholder="0.00" className="mb-4 w-full rounded-xl border border-white/10 bg-black/40 px-3 py-2.5 text-sm text-white outline-none" />
            <button data-testid="admin-fund-submit" onClick={() => doAdjust(adjustFor.address, adjustCat, adjustAmt, "add")} disabled={busy === "adjust" || busy === "deduct"} className="flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-[#0AA84F] to-[#FFA000] py-2.5 text-sm font-bold text-black disabled:opacity-60">
              {busy === "adjust" ? <Loader2 size={15} className="animate-spin" /> : <Plus size={15} />} Add Fund
            </button>
            <button data-testid="admin-fund-deduct" onClick={() => doAdjust(adjustFor.address, adjustCat, adjustAmt, "deduct")} disabled={busy === "adjust" || busy === "deduct"} className="mt-2 flex w-full items-center justify-center gap-2 rounded-xl border border-[#E5484D]/60 bg-[#E5484D]/12 py-2.5 text-sm font-bold text-[#FF8A8D] disabled:opacity-60">
              {busy === "deduct" ? <Loader2 size={15} className="animate-spin" /> : <Minus size={15} />} Deduct Fund
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
